import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import * as FullStory from '@fullstory/browser';
import * as gtag from '@utils/gtag';
import {
  accountState,
  amplitudeEventQueueState,
  amplitudeUserIdSetState,
  hubspotConversationsInitializedState,
  showHubspotChatBubbleState,
} from '@states';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';
import {
  hideHubspotChatBubble,
  showHubspotChatBubble,
} from '@utils/instrumentation';
import { useRouter } from 'next/router';
import amplitude from 'amplitude-js';
import { useIsImpersonator } from '@hooks/auth';

export const useTriggerAmplitudeEvent = (
  name?: string,
  params?: Record<string, any>,
) => {
  const [amplitudeEventQueue, setAmplitudeEventQueue] = useRecoilState(
    amplitudeEventQueueState,
  );

  useEffect(() => {
    if (name) {
      pushAmplitudeEvent({ name, params });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pushAmplitudeEvent = (event: {
    name: string;
    params?: Record<string, any>;
  }) => {
    setAmplitudeEventQueue((eventQueue) => [...eventQueue, event]);
  };

  return {
    amplitudeEventQueue,
    pushAmplitudeEvent,
    setAmplitudeEventQueue,
  };
};

export const useInitializeAmplitude = () => {
  const account = useRecoilValue(accountState);
  const [amplitudeEventQueue, setAmplitudeEventQueue] = useRecoilState(
    amplitudeEventQueueState,
  );
  const [amplitudeUserIdSet, setAmplitudeUserIdSet] = useRecoilState(
    amplitudeUserIdSetState,
  );
  const [initialized, setInitialized] = useState(false);
  const isAdmin = useIsImpersonator();

  useEffect(() => {
    if (!isAdmin && AMPLITUDE_API_KEY && !initialized) {
      amplitude.getInstance().init(AMPLITUDE_API_KEY);
      setInitialized(true);
    }
  }, [initialized, isAdmin]);

  useEffect(() => {
    if (!amplitudeUserIdSet && initialized && account?.primaryDomain) {
      const instance = amplitude.getInstance();
      instance.setUserId(account.primaryDomain);
      setAmplitudeUserIdSet(true);
      if (account.email) {
        const identify = new amplitude.Identify().set('email', account.email);
        instance.identify(identify);
      }
    }
  }, [account, amplitudeUserIdSet, initialized, setAmplitudeUserIdSet]);

  useEffect(() => {
    if (amplitude && amplitudeUserIdSet && amplitudeEventQueue.length > 0) {
      for (const event of amplitudeEventQueue) {
        amplitude.getInstance().logEvent(event.name, event.params);
      }
      setAmplitudeEventQueue([]);
    }
  }, [amplitudeUserIdSet, amplitudeEventQueue, setAmplitudeEventQueue]);
};

export const useInitializeFullStory = () => {
  const isAdmin = useIsImpersonator();
  const account = useRecoilValue(accountState);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!isAdmin && typeof window !== 'undefined' && FULL_STORY_ORG_ID) {
      FullStory.init({
        orgId: FULL_STORY_ORG_ID,
        recordOnlyThisIFrame: true,
      });
      setInitialized(true);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (initialized && account?.primaryDomain) {
      FullStory.identify(account.primaryDomain, {
        email: account.email,
        gid: account.gid,
        primaryDomain: account.primaryDomain,
      });
    }
  }, [account, initialized]);
};

export const useInitializeHubspot = () => {
  const isAdmin = useIsImpersonator();
  const account = useRecoilValue(accountState);
  const [hubspotConversationsInitialized, setHubspotConversationsInitialized] =
    useRecoilState(hubspotConversationsInitializedState);
  const shouldShowHubspotChatBubble = useRecoilValue(
    showHubspotChatBubbleState,
  );
  const { setIdentity } = useTrackingCode();

  useEffect(() => {
    if (!isAdmin && account?.email) {
      setIdentity(account.email);
    }
  }, [account, isAdmin, setIdentity]);

  useEffect(() => {
    if (hubspotConversationsInitialized) {
      if (shouldShowHubspotChatBubble) {
        showHubspotChatBubble();
      } else {
        hideHubspotChatBubble();
      }
    }
  }, [hubspotConversationsInitialized, shouldShowHubspotChatBubble]);

  useEffect(() => {
    if (HUBSPOT_HUB_ID && !isAdmin) {
      if (window) {
        window.hsConversationsOnReady = [
          () => setHubspotConversationsInitialized(false),
        ];
      }
    }
  }, [isAdmin, setHubspotConversationsInitialized]);
};

export const useSetHubspotChatBubbleVisibility = (visible: boolean) => {
  const [shouldShowHubspotChatBubble, setShouldShowHubspotChatBubble] =
    useRecoilState(showHubspotChatBubbleState);

  useEffect(() => {
    if (shouldShowHubspotChatBubble !== visible) {
      setShouldShowHubspotChatBubble(visible);
    }
  }, [setShouldShowHubspotChatBubble, shouldShowHubspotChatBubble, visible]);
};

export const useTrackPageViews = () => {
  const router = useRouter();

  useEffect(() => {
    const routeChangeComplete = (url: string) => {
      gtag.pageview(url);
    };

    router.events.on('routeChangeComplete', routeChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', routeChangeComplete);
    };
  }, [router.events]);
};
